import React, { useState, useEffect } from "react";
import EmpleadoService from "../services/empleados.service";
import AuthService from "../services/auth.service";
import EventBus from "../common/EventBus";
import { Button, ButtonGroup, Row, Col, Modal, Badge, Spinner, Alert, Card } from "react-bootstrap";
import MUIDataTable from "mui-datatables";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { useMediaQuery } from "react-responsive";

const EmpleadosAdmin = () => {
  const currentUser = AuthService.getCurrentUser();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filtro, setFiltro] = useState("todos");

  const [showModal, setShowModal] = useState(false);
  const [empleadoAccion, setEmpleadoAccion] = useState(null);
  const [accion, setAccion] = useState("");

  const fetchEmpleados = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await EmpleadoService.getAllEmpleadosAdmin();
      setEmpleados(response.data || []);
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        err.message ||
        err.toString();
      setError(msg);
      if (err.response && err.response.status === 401) {
        EventBus.dispatch("logout");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchEmpleados();
    } else {
      setLoading(false);
      setError("No token provided!");
    }
  }, []);

  const empleadosFiltrados = empleados.filter((e) => {
    if (filtro === "habilitados") return e.Habilitado === true;
    if (filtro === "deshabilitados") return e.Habilitado === false;
    return true;
  });

  const abrirModal = (empleado, accionTipo) => {
    setEmpleadoAccion(empleado);
    setAccion(accionTipo);
    setShowModal(true);
  };

  const cerrarModal = () => {
    setShowModal(false);
    setEmpleadoAccion(null);
    setAccion("");
  };

  const confirmarAccion = async () => {
    if (!empleadoAccion) return;
    try {
      if (accion === "activar") {
        await EmpleadoService.activateEmpleado(empleadoAccion.DNI);
        toast.success(<div>Empleado activado correctamente</div>, {
          position: "top-center",
          autoClose: 2000,
        });
      } else {
        await EmpleadoService.deactivateEmpleado(empleadoAccion.DNI);
        toast.success(<div>Empleado desactivado correctamente</div>, {
          position: "top-center",
          autoClose: 2000,
        });
      }
      cerrarModal();
      await fetchEmpleados();
    } catch (err) {
      toast.error(
        <div>
          Error al {accion === "activar" ? "activar" : "desactivar"} el
          empleado
        </div>,
        { position: "top-center", autoClose: 2000 }
      );
      if (err.response && err.response.status === 401) {
        EventBus.dispatch("logout");
      }
    }
  };

  if (!currentUser) {
    return (
      <div className="container">
        <header className="jumbotron">
          <h3>No token provided!</h3>
        </header>
      </div>
    );
  }

  const columns = [
    { name: "DNI", label: "DNI" },
    { name: "Nombre", label: "Nombre" },
    { name: "Apellido", label: "Apellido" },
    {
      name: "Habilitado",
      label: "Estado",
      options: {
        customBodyRender: (value) =>
          value === true || value === "true" || value === 1 ? (
            <Badge bg="success">Habilitado</Badge>
          ) : (
            <Badge bg="danger">Deshabilitado</Badge>
          ),
      },
    },
    {
      name: "Opciones",
      label: "Acciones",
      options: {
        customBodyRender: (value, tableMeta) => {
          const dni = tableMeta.rowData[0];
          const empleado = empleados.find((e) => e.DNI === dni);
          const isHabilitado =
            empleado?.Habilitado === true ||
            empleado?.Habilitado === "true" ||
            empleado?.Habilitado === 1;
          return (
            <div>
              {isHabilitado ? (
                <Button
                  onClick={() => abrirModal(empleado, "desactivar")}
                  style={{ backgroundColor: "transparent", border: "none" }}
                >
                  <Tooltip title="Desactivar">
                    <IconButton
                      aria-label="Desactivar"
                      size="large"
                      style={{
                        backgroundColor: "transparent",
                        color: "#f44336",
                        boxShadow: "none",
                      }}
                    >
                      <ToggleOffIcon fontSize="large" />
                    </IconButton>
                  </Tooltip>
                </Button>
              ) : (
                <Button
                  onClick={() => abrirModal(empleado, "activar")}
                  style={{ backgroundColor: "transparent", border: "none" }}
                >
                  <Tooltip title="Activar">
                    <IconButton
                      aria-label="Activar"
                      size="large"
                      style={{
                        backgroundColor: "transparent",
                        color: "#4caf50",
                        boxShadow: "none",
                      }}
                    >
                      <ToggleOnIcon fontSize="large" />
                    </IconButton>
                  </Tooltip>
                </Button>
              )}
            </div>
          );
        },
      },
    },
  ];

  const options = {
    filter: false,
    search: true,
    searchOpen: true,
    searchAlwaysOpen: true,
    searchPlaceholder: "Buscar empleado",
    download: false,
    print: false,
    selectableRows: false,
    viewColumns: false,
    rowsPerPage: 100,
    rowsPerPageOptions: [100],
    enableNestedDataAccess: ".",
  };

  const renderMobileCards = () => (
    <div className="d-flex flex-column" style={{ gap: 16 }}>
      {empleadosFiltrados.map((emp) => {
        const isHabilitado =
          emp.Habilitado === true || emp.Habilitado === "true" || emp.Habilitado === 1;
        return (
          <div
            key={emp.DNI}
            className="d-flex align-items-center justify-content-between"
            style={{
              backgroundColor: "#fff",
              borderRadius: 10,
              padding: "14px 16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <div className="flex-grow-1" style={{ minWidth: 0, paddingRight: 12 }}>
                <div
                className="fw-bold text-truncate"
                style={{ fontSize: "1.05rem", color: "#2c1810" }}
              >
                {emp.Apellido}, {emp.Nombre}
              </div>
              <div style={{ fontSize: "0.85rem", color: "#6c757d" }}>
                DNI {emp.DNI}
              </div>
            </div>
            <div className="d-flex flex-column align-items-end" style={{ gap: 6 }}>
              {isHabilitado ? (
                <Badge bg="success" style={{ fontSize: "0.7rem" }}>
                  Habilitado
                </Badge>
              ) : (
                <Badge bg="danger" style={{ fontSize: "0.7rem" }}>
                  Deshabilitado
                </Badge>
              )}
              {isHabilitado ? (
                <Button
                  variant="outline-danger"
                  size="sm"
                  style={{ fontSize: "0.85rem", padding: "4px 12px" }}
                  onClick={() => abrirModal(emp, "desactivar")}
                >
                  Desactivar
                </Button>
              ) : (
                <Button
                  variant="outline-success"
                  size="sm"
                  style={{ fontSize: "0.85rem", padding: "4px 12px" }}
                  onClick={() => abrirModal(emp, "activar")}
                >
                  Activar
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="container mt-3">
      <h3 style={{ color: "#fff" }}>Administraci&oacute;n de Empleados</h3>

      <Row className="mb-3 align-items-center">
        <Col>
          <ButtonGroup
            size={isMobile ? "sm" : undefined}
            className="w-100"
            style={{ maxWidth: isMobile ? "100%" : 480, color: "#fff" }}
          >
            <Button
              variant={filtro === "todos" ? "primary" : "outline-primary"}
              style={{ color: "#fff" }}
              onClick={() => setFiltro("todos")}
            >
              Todos
            </Button>
            <Button
              variant={
                filtro === "habilitados" ? "success" : "outline-success"
              }
              style={{ color: "#fff" }}
              onClick={() => setFiltro("habilitados")}
            >
              Habilitados
            </Button>
            <Button
              variant={
                filtro === "deshabilitados" ? "danger" : "outline-danger"
              }
              style={{ color: "#fff" }}
              onClick={() => setFiltro("deshabilitados")}
            >
              Deshabilitados
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      {loading ? (
        <Row className="justify-content-center py-4">
          <Spinner animation="border" role="status" />
        </Row>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : isMobile ? (
        renderMobileCards()
      ) : (
        <MUIDataTable
          title={"Empleados"}
          data={empleadosFiltrados}
          columns={columns}
          options={options}
        />
      )}

      <Modal show={showModal} onHide={cerrarModal} centered>
        <Modal.Header>
          <Modal.Title>Confirmar acci&oacute;n</Modal.Title>
          <Button variant="outline-danger" onClick={cerrarModal}>
            X
          </Button>
        </Modal.Header>
        <Modal.Body>
          &iquest;Desea {accion === "activar" ? "activar" : "desactivar"} al
          empleado{" "}
          <strong>
            {empleadoAccion?.Nombre} {empleadoAccion?.Apellido}
          </strong>{" "}
          (DNI: {empleadoAccion?.DNI})?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={cerrarModal}>
            Cancelar
          </Button>
          <Button
            variant={accion === "activar" ? "success" : "danger"}
            onClick={confirmarAccion}
          >
            {accion === "activar" ? "Activar" : "Desactivar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmpleadosAdmin;
